/**
 * RicercatoreSidebar — sidebar for the Ricercatore portal.
 * Six flat navigation items matching the B3 specification, logout.
 */

import { Link, useLocation } from 'react-router-dom'
import {
  X,
  Home,
  BookOpen,
  BookMarked,
  Users,
  FolderOpen,
  Settings,
  LogOut,
  Scale,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'

interface RicercatoreSidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
  exact?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/ricerca', icon: Home, exact: true },
  { label: 'Bandi di ricerca', path: '/ricerca/bandi', icon: BookOpen },
  { label: 'Letteratura ARC', path: '/ricerca/letteratura', icon: BookMarked },
  { label: 'Brevetti & IP', path: '/ricerca/brevetti', icon: Scale },
  { label: 'Partner & Consorzi', path: '/ricerca/partner', icon: Users },
  { label: 'I miei progetti', path: '/ricerca/progetti', icon: FolderOpen },
  { label: 'Impostazioni', path: '/profile', icon: Settings, exact: true },
]

export function RicercatoreSidebar({ isOpen, onClose }: RicercatoreSidebarProps) {
  const location = useLocation()
  const { logout } = useAuth()

  const isActive = (item: NavItem) => {
    // For items with query strings, match only on the pathname part
    const itemPath = item.path.split('?')[0]
    if (item.exact) return location.pathname === itemPath
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/')
  }

  const navLinkClass = (item: NavItem) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
      isActive(item)
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    )

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-64 border-r bg-background transition-transform lg:translate-x-0 lg:static',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Mobile header */}
          <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
            <span className="font-semibold">Menu</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Desktop brand */}
          <div className="hidden h-16 items-center border-b px-4 lg:flex">
            <Link to="/ricerca" className="flex items-center gap-2" onClick={onClose}>
              <span className="text-lg font-bold tracking-tight text-primary">
                Research<span className="text-foreground">Constellation</span>
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto p-4">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={navLinkClass(item)}
                  onClick={onClose}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {/* Logout */}
          <div className="border-t p-4">
            <button
              onClick={() => {
                onClose()
                logout()
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
